import type { TFunction } from "i18next";
import { format, isValid, parseISO } from "date-fns";

import { DoseUnits } from "gql/graphql";

import { formatRxSchedule } from "../formatRxSchedule";
import type { TreatmentOverview } from "../hooks/useTreatmentOverviews";
import type { TreatmentProductLockupVariant } from "../TreatmentProductLockup";

type RxTreatmentOverview = Extract<
  TreatmentOverview,
  { __typename: "RxTreatmentOverview" }
>;

type MedicationQuantity = {
  amount: number;
  unit: DoseUnits;
};

export type DoseSummaryLockupProps = {
  variant: TreatmentProductLockupVariant;
  title?: string;
  eyebrow?: string;
  subtitle?: string;
};

const localizeUnit = (
  unit: DoseUnits,
  amount: number,
  t: TFunction<"care">
): string =>
  unit === DoseUnits.Each
    ? t("unitsTakenLabel", { count: amount })
    : t(`doseUnits.${unit}`, {
        defaultValue: unit.toLowerCase(),
      });

const formatDosageInParens = (
  dosage: MedicationQuantity,
  t: TFunction<"care">
): string =>
  t("detail.doseSummary.dosageInParens", {
    amount: dosage.amount,
    unit: localizeUnit(dosage.unit, dosage.amount, t),
  });

const formatTakenOnSubtitle = (
  takenOn: string,
  t: TFunction<"care">
): string | undefined => {
  const parsed = parseISO(takenOn);
  if (!isValid(parsed)) {
    return undefined;
  }
  return t("detail.doseSummary.onDate", {
    date: format(parsed, "EEE, MMMM d, yyyy"),
  });
};

const fromUnitsAndDosage = (
  unitsTaken: number,
  dosage: MedicationQuantity,
  t: TFunction<"care">,
  subtitle?: string
): DoseSummaryLockupProps => ({
  variant: "doseSummary",
  title: t("detail.doseSummary.unitsTaken", {
    count: unitsTaken,
    unitsTakenLabel: t("unitsTakenLabel", { count: unitsTaken }),
  }),
  eyebrow: formatDosageInParens(dosage, t),
  subtitle,
});

const fromDosageOnly = (
  dosage: MedicationQuantity,
  t: TFunction<"care">,
  subtitle?: string
): DoseSummaryLockupProps => ({
  variant: "doseSummary",
  title: t("detail.doseSummary.dosageOnly", {
    amount: dosage.amount,
    unit: localizeUnit(dosage.unit, dosage.amount, t),
  }),
  subtitle,
});

/**
 * Maps dose fields onto `TreatmentProductLockup`'s `doseSummary` props.
 *
 * Preference:
 * 1. `latestDoseTaken` — last logged dose (units + mg + date)
 * 2. Medication logging unsupported — treatment cadence (`cadence` variant,
 *    paragraph md / regular so it does not share the dose-amount heading)
 * 3. `initialDose` — starting prescribed dose (syringe units + dosage)
 *
 * Layout (matches existing lockup doseSummary tests / Figma):
 *   title    — "8 units"
 *   eyebrow  — "(0.2 mg)"
 *   subtitle — "on Thu, July 7, 2026" (last dose only)
 */
export const getDoseSummaryLockupProps = (
  treatment: TreatmentOverview,
  t: TFunction<"care">
): DoseSummaryLockupProps | undefined => {
  if (treatment.__typename !== "RxTreatmentOverview") {
    return undefined;
  }

  const rx = treatment as RxTreatmentOverview;
  const { latestDoseTaken, initialDose } = rx;

  if (latestDoseTaken) {
    const { unitsTaken, dosage, takenOn } = latestDoseTaken;
    const subtitle = formatTakenOnSubtitle(takenOn, t);

    if (unitsTaken != null) {
      return fromUnitsAndDosage(unitsTaken, dosage, t, subtitle);
    }
    return fromDosageOnly(dosage, t, subtitle);
  }

  if (rx.isMedicationLogSupported !== true) {
    const cadence = formatRxSchedule(rx, t);
    return cadence ? { variant: "cadence", title: cadence } : undefined;
  }

  if (!initialDose?.dosage) {
    return undefined;
  }

  const { dosage, doseSyringeUnits } = initialDose;

  if (doseSyringeUnits != null) {
    return fromUnitsAndDosage(doseSyringeUnits, dosage, t);
  }

  return fromDosageOnly(dosage, t);
};
